import { Link, Navigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { useAuthStore } from "@/stores/authStore";
import { fetchForumHubCourses } from "@/services/api/instructor";

export function TenantForumPage() {
  const { tenantSlug = "" } = useParams<{ tenantSlug: string }>();
  const t = useTranslations("forum");
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  const forumQuery = useQuery({
    queryKey: ["forum-hub", tenantSlug],
    queryFn: fetchForumHubCourses,
    enabled: isAuthenticated,
  });

  if (!isAuthenticated) {
    return <Navigate to={`/t/${tenantSlug}/login`} replace />;
  }

  if (forumQuery.isLoading) {
    return (
      <main className="mx-auto max-w-3xl px-4 py-10" data-testid="forum-hub-loading">
        <p className="text-sm text-[var(--text-muted)]">{t("loading")}</p>
      </main>
    );
  }

  const courses = forumQuery.data ?? [];

  return (
    <main className="mx-auto max-w-3xl px-4 py-10" data-testid="forum-hub">
      <h1 className="text-2xl font-semibold">{t("hubTitle")}</h1>
      <p className="mt-1 text-sm text-[var(--text-muted)]">{t("hubHint")}</p>

      {forumQuery.isError ? (
        <p role="alert" className="mt-8 text-sm text-[var(--danger)]" data-testid="forum-hub-error">
          {t("loadFailed")}
        </p>
      ) : courses.length === 0 ? (
        <p className="mt-8 text-sm text-[var(--text-muted)]" data-testid="forum-hub-empty">
          {t("hubEmpty")}
        </p>
      ) : (
        <ul className="mt-8 space-y-3" data-testid="forum-hub-list">
          {courses.map((course) => (
            <li key={course.id}>
              <Link
                to={`/t/${tenantSlug}/learn/${course.slug}/community`}
                className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-[var(--border)] px-4 py-3 text-sm hover:bg-[var(--surface-3)]"
                data-testid={`forum-hub-course-${course.slug}`}
              >
                <span className="font-medium">{course.title}</span>
                <span className="text-[var(--text-muted)]">
                  {t("hubThreadCount", { count: course.threadCount })}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}
